/**
 * RingFloatingSoftphone — the compact dialer that floats OVER the Ring
 * Center conversation area. Drives the SHARED useCall() bus — it owns no
 * call logic. Supports: dial, in-call controls, warm transfer (colleague
 * by name/extension, department, or a typed desk extension), blind
 * transfer, merge/add-call, call park (label + color per slot) and pickup.
 */
import { useState, useEffect, useRef } from "react";
import {
  MicOff, LayoutGrid, Volume2, Pause, PhoneForwarded, UserPlus, Circle,
  ParkingSquare, PhoneOff, X, Phone, Delete, Search, Check, PhoneIncoming,
  Users, Building2, Pencil, Star, Trash2, Grid3X3, StickyNote,
} from "lucide-react";
import { useCall } from "../../components/layout/ActiveCallOverlay";
import { callsAPI, speedDialAPI } from "../../services/api";
import { formatPhoneNumber } from "../../lib/utils";
import { toast } from "sonner";

const NAVY = "#032866";
const NAVY_DEEP = "#002244";
const MUTED = "#7B8FA3";
const HAIRLINE = "rgba(3,40,102,0.08)";
const ROYAL = "#2A7CC4";
const RED = "#C0392B";
const GREEN = "#1D9E75";
const AMBER = "#C77D0A";
const FONT_HEAD = "'Manrope', sans-serif";
const FONT_BODY = "'IBM Plex Sans', sans-serif";
const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];
const DIAL_KEYS = [
  { k: "1", s: "" }, { k: "2", s: "ABC" }, { k: "3", s: "DEF" },
  { k: "4", s: "GHI" }, { k: "5", s: "JKL" }, { k: "6", s: "MNO" },
  { k: "7", s: "PQRS" }, { k: "8", s: "TUV" }, { k: "9", s: "WXYZ" },
  { k: "*", s: "" }, { k: "0", s: "+" }, { k: "#", s: "" },
];

// Progressive phone formatter for the dialer display. Keeps the raw entry
// in state; this only prettifies what the agent sees as they type.
function prettyDial(raw) {
  if (!raw) return "";
  if (/[*#]/.test(raw)) return raw;
  const hasPlus = raw.trim().startsWith("+");
  const d = raw.replace(/\D/g, "");
  if (!d) return hasPlus ? "+" : "";
  if (!hasPlus) {
    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    if (d.length <= 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)} ${d.slice(10)}`;
  }
  if (d.startsWith("1") && d.length <= 11) {
    const r = d.slice(1);
    if (!r) return "+1";
    if (r.length <= 3) return `+1 ${r}`;
    if (r.length <= 6) return `+1 (${r.slice(0, 3)}) ${r.slice(3)}`;
    return `+1 (${r.slice(0, 3)}) ${r.slice(3, 6)}-${r.slice(6, 10)}`;
  }
  return `+${d}`;
}

// Park-slot color palette — keys match the backend PARK_SLOT_COLORS set.
const COLOR_HEX = { navy: NAVY, green: GREEN, amber: AMBER, red: RED, royal: ROYAL, slate: "#64748B" };
const PALETTE = ["slate", "navy", "green", "amber", "red", "royal"];
const slotHex = (c) => COLOR_HEX[c] || COLOR_HEX.slate;

function initials(name, phone) {
  const s = (name || "").trim();
  if (s && /[a-z]/i.test(s)) { const p = s.split(/\s+/); return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase(); }
  const d = (phone || "").replace(/\D/g, "");
  return d.slice(-2) || "?";
}
function mmss(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  return `${Math.floor(s / 60).toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;
}

export default function RingFloatingSoftphone({ onClose, initialTo = "", fromNumber = "" }) {
  const {
    deviceReady, initializeDevice, activeCall, callDuration,
    isMuted, isOnHold, isRecording, isMerging,
    makeCall, endCall, toggleMute, toggleHold, transferCall, mergeCall, toggleRecording, sendDTMF,
    warmTransferToColleague, warmTransferToNumber, transferToDepartment, departments = [],
    completeWarmTransfer, cancelWarmTransfer, warmTransfer, warmTransferTargets = [],
    parkSlots = [], parkActiveCall, refreshParkSlots, pickupParkedCall,
    setSuppressOverlay,
    callNotes = { key_points: "", action_items: "" }, setCallNotes,
  } = useCall();

  const [dialTo, setDialTo] = useState(initialTo || "");
  const [showKeypad, setShowKeypad] = useState(false);
  // panel: false | "transfer" | "merge" | "park" | "notes"
  const [panel, setPanel] = useState(false);
  const [transferMode, setTransferMode] = useState("warm"); // "warm" | "blind"
  const [search, setSearch] = useState("");
  const [target, setTarget] = useState("");     // blind-transfer / merge number
  const [extNumber, setExtNumber] = useState(""); // warm-transfer extension
  const [editParkSlots, setEditParkSlots] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [dialView, setDialView] = useState("keypad"); // "keypad" | "favorites"
  const [dialTouched, setDialTouched] = useState(false); // true once the agent edits the prefilled number
  const [favPrompt, setFavPrompt] = useState(null); // { number } while naming a new favorite
  const [favNameDraft, setFavNameDraft] = useState("");

  const warmActive = warmTransfer && warmTransfer.state && warmTransfer.state !== "idle";
  const occupiedSlots = (parkSlots || []).filter((s) => s.occupied);

  useEffect(() => { refreshParkSlots?.(); }, [refreshParkSlots]);
  // Suppress the global ActiveCallOverlay while this richer softphone is mounted,
  // so only ONE active-call widget is visible at a time.
  useEffect(() => {
    setSuppressOverlay?.(true);
    return () => setSuppressOverlay?.(false);
  }, [setSuppressOverlay]);
  useEffect(() => { speedDialAPI.getAll().then((r) => setFavorites(r.data || [])).catch(() => {}); }, []);

  const toE164 = (raw) => {
    const d = (raw || "").replace(/\D/g, "");
    if (d.length === 10) return `+1${d}`;
    if (d.length === 11 && d[0] === "1") return `+${d}`;
    return ((raw || "").trim().startsWith("+") ? "+" : "") + d;
  };
  const openFavPrompt = () => {
    const num = dialTo.trim();
    if (!num) return;
    setFavNameDraft(prettyDial(num) || num);
    setFavPrompt({ number: num });
  };
  const confirmFavorite = async () => {
    if (!favPrompt) return;
    const num = favPrompt.number;
    const nm = (favNameDraft || "").trim() || prettyDial(num) || num;
    try {
      const r = await speedDialAPI.create({ name: nm, phone_number: toE164(num), position: favorites.length });
      setFavorites((prev) => [...prev, r.data]);
      toast.success("Added to favorites");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save favorite"); }
    finally { setFavPrompt(null); setFavNameDraft(""); }
  };
  const removeFavorite = async (id) => {
    try { await speedDialAPI.delete(id); setFavorites((prev) => prev.filter((f) => f.id !== id)); toast.success("Removed from favorites"); }
    catch { toast.error("Could not remove favorite"); }
  };
  const callFavorite = (f) => {
    if (!deviceReady) { toast.error("Connect the phone first"); initializeDevice({ isUserGesture: true }); return; }
    makeCall(f.phone_number, fromNumber || undefined);
  };
  // First keypad press after opening on a pre-filled number starts fresh
  // (so digits don't append to the selected caller's number).
  const pressKey = (k) => { setDialTo((v) => (dialTouched ? v + k : k)); setDialTouched(true); };

  const callName = activeCall?.company?.name || activeCall?.contact?.name || "";
  const callNumber = activeCall?.number || activeCall?.phone || dialTo;

  const saveSlotConfig = async (slot, patch) => {
    try { await callsAPI.setParkSlotConfig(slot, patch); refreshParkSlots?.(); } catch (e) { /* toast handled upstream */ }
  };

  const slotLabel = (s) => (s.label && s.label.trim()) ? `P${s.slot} · ${s.label}` : `Park ${s.slot}`;

  // ── IDLE — dialer + parked-calls pickup tray ─────────────────
  if (!activeCall) {
    return (
      <Shell title="Dialer" onClose={onClose}>
        <div className="w-full relative">
          {favPrompt && (
            <div className="absolute inset-0 z-10 rounded-xl flex flex-col items-center justify-center px-4" style={{ background: "rgba(255,255,255,0.97)" }} data-testid="rc-fav-name-prompt">
              <div className="text-[13px] font-bold mb-0.5" style={{ color: NAVY_DEEP }}>Name this favorite</div>
              <div className="text-[11px] mb-3" style={{ color: MUTED }}>{prettyDial(favPrompt.number)}</div>
              <input
                autoFocus
                data-testid="rc-fav-name-input"
                value={favNameDraft}
                onChange={(e) => setFavNameDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") confirmFavorite(); if (e.key === "Escape") setFavPrompt(null); }}
                placeholder="e.g. Dr. Smith office"
                className="w-full h-10 px-3 rounded-lg text-[14px] text-center focus:outline-none mb-3"
                style={{ border: `1px solid ${HAIRLINE}`, color: NAVY_DEEP }}
              />
              <div className="flex gap-2 w-full">
                <button data-testid="rc-fav-name-cancel" onClick={() => setFavPrompt(null)} className="flex-1 h-10 rounded-lg text-[13px] font-bold hover:bg-slate-50" style={{ border: `1px solid ${HAIRLINE}`, color: MUTED }}>Cancel</button>
                <button data-testid="rc-fav-name-save" onClick={confirmFavorite} className="flex-1 h-10 rounded-lg text-white text-[13px] font-bold hover:brightness-95" style={{ background: GREEN }}>Save</button>
              </div>
            </div>
          )}
          {/* Keypad / Favorites toggle */}
          <div className="flex rounded-lg overflow-hidden mb-3" style={{ border: `1px solid ${HAIRLINE}` }}>
            <button data-testid="rc-dial-tab-keypad" onClick={() => setDialView("keypad")} className="flex-1 h-8 text-[12px] font-bold flex items-center justify-center gap-1.5 transition-colors" style={dialView === "keypad" ? { background: NAVY_DEEP, color: "#fff" } : { color: MUTED }}><Grid3X3 size={13} /> Keypad</button>
            <button data-testid="rc-dial-tab-favorites" onClick={() => setDialView("favorites")} className="flex-1 h-8 text-[12px] font-bold flex items-center justify-center gap-1.5 transition-colors" style={dialView === "favorites" ? { background: NAVY_DEEP, color: "#fff" } : { color: MUTED }}><Star size={13} /> Favorites{favorites.length ? ` · ${favorites.length}` : ""}</button>
          </div>

          {dialView === "keypad" ? (
            <>
              {/* Number display with save-to-favorites + inline backspace */}
              <div className="relative mb-3">
                <button
                  data-testid="rc-dial-add-fav"
                  onClick={openFavPrompt}
                  disabled={!dialTo.trim()}
                  className="absolute left-1.5 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center rounded-md hover:bg-slate-100 disabled:opacity-30 transition-colors"
                  style={{ color: MUTED }}
                  title="Save to favorites"
                >
                  <Star size={16} />
                </button>
                <input
                  data-testid="rc-dial-input"
                  value={prettyDial(dialTo)}
                  onChange={(e) => { setDialTouched(true); setDialTo(e.target.value.replace(/[^\d*#+]/g, "")); }}
                  placeholder="Enter a number"
                  inputMode="tel"
                  className="w-full text-center text-[22px] font-bold rounded-lg py-3 pl-9 pr-9 focus:outline-none"
                  style={{ color: NAVY_DEEP, border: `1px solid ${HAIRLINE}`, fontFamily: FONT_HEAD }}
                />
                {dialTo && (
                  <button
                    data-testid="rc-dial-backspace"
                    onClick={() => { setDialTouched(true); setDialTo((v) => v.slice(0, -1)); }}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center rounded-md hover:bg-slate-100 transition-colors"
                    style={{ color: MUTED }}
                    title="Delete last digit"
                  >
                    <Delete size={18} />
                  </button>
                )}
              </div>

              {/* Keypad — big tappable keys with letter sublabels */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                {DIAL_KEYS.map(({ k, s }) => (
                  <button
                    key={k}
                    data-testid={`rc-key-${k}`}
                    onClick={() => pressKey(k)}
                    className="h-14 rounded-2xl flex flex-col items-center justify-center transition-transform duration-150 hover:bg-slate-50 active:scale-95"
                    style={{ background: "rgba(255,255,255,0.92)", border: `1px solid ${HAIRLINE}`, boxShadow: "0 1px 4px rgba(3,40,102,0.06)" }}
                  >
                    <span className="text-[20px] font-bold leading-none" style={{ color: NAVY_DEEP }}>{k}</span>
                    {s ? <span className="text-[8px] font-bold tracking-[1.5px] mt-1" style={{ color: MUTED }}>{s}</span> : null}
                  </button>
                ))}
              </div>

              {/* Primary action */}
              {deviceReady ? (
                <button
                  data-testid="rc-dial-call"
                  onClick={() => dialTo.trim() && makeCall(dialTo.trim(), fromNumber || undefined)}
                  disabled={!dialTo.trim()}
                  className="w-full grid place-items-center text-white font-bold text-[15px] rounded-full disabled:opacity-40 transition-transform duration-200 active:scale-[0.98] hover:brightness-95"
                  style={{ background: GREEN, height: 54, boxShadow: `0 10px 22px ${GREEN}4D` }}
                >
                  <span className="flex items-center gap-2"><Phone size={18} /> Call</span>
                </button>
              ) : (
                <button
                  data-testid="rc-connect-phone"
                  onClick={() => initializeDevice({ isUserGesture: true })}
                  className="w-full rounded-lg text-white text-[13px] font-bold flex items-center justify-center gap-2 hover:brightness-95"
                  style={{ background: NAVY, height: 48 }}
                >
                  <Phone size={15} /> Connect phone to call
                </button>
              )}
              {!deviceReady && (
                <div className="text-[10.5px] text-center mt-2 leading-snug" style={{ color: MUTED }}>
                  Connect once to place and receive calls on this browser.
                </div>
              )}
            </>
          ) : (
            /* ── Favorites — one-tap speed dial ── */
            <div data-testid="rc-dial-favorites">
              {favorites.length === 0 ? (
                <div className="text-center py-10 px-4 text-[12px]" style={{ color: MUTED }} data-testid="rc-fav-empty">
                  <Star size={24} className="mx-auto mb-2" style={{ color: "#CBD5E1" }} />
                  No favorites yet. On the keypad, type a number and tap the star to save it here for one-tap calling.
                </div>
              ) : (
                <div className="flex flex-col gap-1.5 max-h-[340px] overflow-y-auto">
                  {favorites.map((f) => (
                    <div key={f.id} data-testid={`rc-fav-${f.id}`} className="flex items-center gap-2 p-2 rounded-lg" style={{ border: `1px solid ${HAIRLINE}` }}>
                      <div className="w-9 h-9 rounded-full grid place-items-center text-white text-[12px] font-bold shrink-0" style={{ background: ROYAL }}>{initials(f.name, f.phone_number)}</div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-bold truncate" style={{ color: NAVY_DEEP }}>{f.name}</div>
                        <div className="text-[11px] truncate" style={{ color: MUTED }}>{formatPhoneNumber(f.phone_number)}</div>
                      </div>
                      <button onClick={() => callFavorite(f)} data-testid={`rc-fav-call-${f.id}`} className="w-9 h-9 rounded-full grid place-items-center text-white shrink-0 hover:brightness-95" style={{ background: GREEN }} title={`Call ${f.name}`}><Phone size={15} /></button>
                      <button onClick={() => removeFavorite(f.id)} data-testid={`rc-fav-remove-${f.id}`} className="w-7 h-7 rounded-md grid place-items-center shrink-0 hover:bg-slate-100" style={{ color: MUTED }} title="Remove favorite"><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Parked callers waiting for pickup */}
          {occupiedSlots.length > 0 && (
            <div className="w-full mt-4 pt-3" style={{ borderTop: `1px solid ${HAIRLINE}` }} data-testid="rc-parked-tray">
              <div className="text-[11px] font-bold uppercase tracking-widest mb-2" style={{ color: MUTED }}>Parked calls</div>
              {occupiedSlots.map((s) => (
                <div key={s.slot} className="flex items-center gap-2 mb-1.5" data-testid={`rc-parked-slot-${s.slot}`}>
                  <div className="w-8 h-7 rounded-md grid place-items-center text-[11px] font-bold shrink-0 text-white" style={{ background: slotHex(s.color) }}>P{s.slot}</div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] font-semibold truncate" style={{ color: NAVY_DEEP }}>{s.label ? s.label : (formatPhoneNumber(s.caller_number) || "Unknown")}</div>
                    <div className="text-[10px] truncate" style={{ color: MUTED }}>{s.label ? (formatPhoneNumber(s.caller_number) || "Unknown") : (s.parked_by_name ? `by ${s.parked_by_name}` : "parked")}</div>
                  </div>
                  <button data-testid={`rc-park-pickup-${s.slot}`} onClick={() => pickupParkedCall(s.slot)}
                    className="h-8 px-3 rounded-lg text-[12px] font-bold text-white flex items-center gap-1" style={{ background: GREEN }}>
                    <PhoneIncoming size={13} /> Pick up
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Shell>
    );
  }

  // ── ACTIVE — control grid ────────────────────────────────────
  const controls = [
    { key: "mute", icon: MicOff, label: "Mute", active: isMuted, onClick: toggleMute },
    { key: "keypad", icon: LayoutGrid, label: "Keypad", active: showKeypad, onClick: () => setShowKeypad((v) => !v) },
    { key: "speaker", icon: Volume2, label: "Speaker", onClick: () => {} },
    { key: "hold", icon: Pause, label: "Hold", hot: true, active: isOnHold, onClick: toggleHold },
    { key: "transfer", icon: PhoneForwarded, label: "Transfer", hot: true, onClick: () => { setPanel("transfer"); setTransferMode("warm"); setShowKeypad(false); } },
    { key: "add", icon: UserPlus, label: "Add call", onClick: () => { setPanel("merge"); setShowKeypad(false); } },
    { key: "record", icon: Circle, label: "Record", active: isRecording, activeColor: RED, onClick: toggleRecording },
    { key: "park", icon: ParkingSquare, label: "Park", hot: true, onClick: () => { refreshParkSlots?.(); setEditParkSlots(false); setPanel("park"); setShowKeypad(false); } },
    { key: "merge", icon: Users, label: "Merge", active: isMerging, onClick: () => { setPanel("merge"); setShowKeypad(false); } },
    { key: "notes", icon: StickyNote, label: "Notes", active: panel === "notes", onClick: () => { setPanel(panel === "notes" ? false : "notes"); setShowKeypad(false); } },
  ];

  const submitNumber = () => {
    const t = target.trim();
    if (!t) return;
    if (panel === "transfer") transferCall(t); else mergeCall(t);
    setTarget(""); setPanel(false);
  };

  const submitExtension = () => {
    const t = extNumber.trim();
    if (!t) return;
    warmTransferToNumber(t);
    setExtNumber(""); setPanel(false);
  };

  // One-tap transfer to a saved speed-dial favorite. Honors the selected
  // Warm/Blind mode: warm consults first (showing the favorite's name in
  // the banner), blind sends the caller straight through.
  const transferToFavorite = (f) => {
    if (!f?.phone_number) return;
    if (transferMode === "blind") transferCall(f.phone_number);
    else warmTransferToNumber(f.phone_number, f.name);
    setPanel(false); setSearch("");
  };

  const q = search.trim().toLowerCase();
  const filteredFavs = (favorites || []).filter((f) => !q || `${f.name || ""} ${f.phone_number || ""}`.toLowerCase().includes(q));
  const filteredTargets = (warmTransferTargets || []).filter((t) => {
    if (!q) return true;
    const s = `${t.first_name || ""} ${t.last_name || ""} ${t.email || ""} ${t.extension || ""}`.toLowerCase();
    return s.includes(q);
  });
  const filteredDepts = (departments || []).filter((d) => !q || (d.name || "").toLowerCase().includes(q));

  return (
    <Shell title="Active call" onClose={onClose}>
      <div className="relative mb-2">
        <div className="w-16 h-16 rounded-full grid place-items-center text-white text-lg font-black" style={{ background: `linear-gradient(135deg, ${ROYAL} 0%, ${NAVY} 100%)`, boxShadow: `0 8px 20px rgba(3,40,102,0.28)`, fontFamily: FONT_HEAD }}>{initials(callName, callNumber)}</div>
        <div className="absolute inset-0 rounded-full pointer-events-none" style={{ border: "2px solid rgba(255,218,0,0.55)" }} />
      </div>
      <div className="text-[19px] font-bold" style={{ color: NAVY_DEEP, fontFamily: FONT_HEAD }}>{callName || formatPhoneNumber(callNumber)}</div>
      <div className="text-[12px]" style={{ color: MUTED }}>{formatPhoneNumber(callNumber)}</div>
      <div className="flex items-center gap-1.5 mt-2 mb-4 px-3 py-1 rounded-full text-[12px] font-bold" style={{ color: isOnHold ? AMBER : GREEN, background: isOnHold ? "rgba(199,125,10,0.1)" : "rgba(29,158,117,0.1)" }}>
        <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: isOnHold ? AMBER : GREEN }} /> {isOnHold ? "On hold" : "In call"} · {mmss(callDuration)}
      </div>

      {/* Warm-transfer consult banner */}
      {warmActive ? (
        <div data-testid="rc-warm-banner" className="w-full mb-4 rounded-xl px-4 py-3" style={{ background: "rgba(3,40,102,0.05)", border: `1px solid ${NAVY}` }}>
          <div className="text-[13px] font-bold" style={{ color: NAVY_DEEP, fontFamily: FONT_HEAD }}>
            {warmTransfer.state === "calling" ? "Ringing " : "Consulting with "}{warmTransfer.targetName}
          </div>
          <div className="text-[11px] mt-0.5 mb-2.5" style={{ color: MUTED }}>
            Caller is on hold with music.{warmTransfer.state === "consulting" ? " Brief them, then complete or cancel." : " Waiting for pickup…"}
          </div>
          <div className="flex gap-2">
            <button data-testid="rc-warm-cancel" onClick={cancelWarmTransfer} className="flex-1 h-9 rounded-lg text-[13px] font-semibold" style={{ border: `1px solid ${HAIRLINE}`, color: NAVY }}>Cancel</button>
            <button data-testid="rc-warm-complete" onClick={completeWarmTransfer} disabled={warmTransfer.state !== "consulting"} className="flex-1 h-9 rounded-lg text-[13px] font-bold text-white disabled:opacity-50 flex items-center justify-center gap-1.5" style={{ background: GREEN }}><Check size={14} /> Complete</button>
          </div>
        </div>
      ) : panel === "notes" ? (
        <div className="w-full mb-4" data-testid="rc-notes-panel">
          <div className="flex items-center gap-1.5 mb-2">
            <StickyNote size={13} style={{ color: NAVY }} />
            <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: MUTED }}>Call Notes</span>
          </div>
          <textarea
            data-testid="rc-notes-key-points"
            value={callNotes.key_points || ""}
            onChange={(e) => setCallNotes?.((n) => ({ ...n, key_points: e.target.value }))}
            rows={3}
            placeholder="Key points from the call…"
            className="w-full rounded-lg border px-3 py-2 text-[13px] mb-2 focus:outline-none resize-none"
            style={{ borderColor: HAIRLINE, color: NAVY_DEEP }}
          />
          <textarea
            data-testid="rc-notes-action-items"
            value={callNotes.action_items || ""}
            onChange={(e) => setCallNotes?.((n) => ({ ...n, action_items: e.target.value }))}
            rows={2}
            placeholder="Action items / follow-ups…"
            className="w-full rounded-lg border px-3 py-2 text-[13px] mb-2.5 focus:outline-none resize-none"
            style={{ borderColor: HAIRLINE, color: NAVY_DEEP }}
          />
          <div className="text-[10px] mb-2.5 leading-tight" style={{ color: MUTED }}>Notes are saved to the call log automatically when the call ends.</div>
          <button data-testid="rc-notes-done" onClick={() => setPanel(false)} className="w-full h-9 rounded-lg text-[13px] font-bold text-white" style={{ background: NAVY }}>Done</button>
        </div>
      ) : panel === "transfer" ? (
        <div className="w-full mb-4" data-testid="rc-transfer-panel">
          <div className="flex rounded-lg overflow-hidden mb-2.5" style={{ border: `1px solid ${HAIRLINE}` }}>
            <button data-testid="rc-transfer-mode-warm" onClick={() => setTransferMode("warm")} className="flex-1 h-8 text-[12px] font-bold" style={{ background: transferMode === "warm" ? NAVY : "#fff", color: transferMode === "warm" ? "#fff" : MUTED }}>Warm</button>
            <button data-testid="rc-transfer-mode-blind" onClick={() => setTransferMode("blind")} className="flex-1 h-8 text-[12px] font-bold" style={{ background: transferMode === "blind" ? NAVY : "#fff", color: transferMode === "blind" ? "#fff" : MUTED }}>Blind</button>
          </div>
          <div className="text-[11px] mb-2.5 leading-snug" style={{ color: MUTED }}>
            {transferMode === "warm"
              ? "Warm: talk to the person first, then connect the caller."
              : "Blind: send the caller straight through — no consult."}
          </div>

          {transferMode === "warm" ? (
            <div>
              <div className="relative mb-2">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: MUTED }} />
                <input data-testid="rc-transfer-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, extension, or department"
                  className="w-full rounded-lg border pl-8 pr-3 py-2 text-[13px] focus:outline-none" style={{ borderColor: HAIRLINE, color: NAVY_DEEP }} />
              </div>
              <div className="max-h-40 overflow-y-auto -mx-1 px-1" data-testid="rc-transfer-target-list">
                {/* Favorites — one-tap transfer */}
                {filteredFavs.length > 0 && (
                  <>
                    <div className="text-[10px] font-bold uppercase tracking-widest px-1 py-1 flex items-center gap-1" style={{ color: MUTED }}><Star size={10} style={{ color: "#C9A800", fill: "#FFDA00" }} /> Favorites</div>
                    {filteredFavs.map((f) => (
                      <button key={f.id} data-testid={`rc-transfer-fav-${f.id}`} onClick={() => transferToFavorite(f)}
                        className="w-full flex items-center gap-2.5 py-2 px-1.5 rounded-lg hover:bg-slate-50 text-left">
                        <div className="w-8 h-8 rounded-full grid place-items-center text-white text-[11px] font-bold shrink-0" style={{ background: GREEN }}>{initials(f.name, f.phone_number)}</div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] font-semibold truncate" style={{ color: NAVY_DEEP }}>{f.name}</div>
                          <div className="text-[11px] truncate" style={{ color: MUTED }}>{formatPhoneNumber(f.phone_number)}</div>
                        </div>
                        <Star size={13} className="shrink-0" style={{ color: "#C9A800", fill: "#FFDA00" }} />
                      </button>
                    ))}
                    {(filteredDepts.length > 0 || filteredTargets.length > 0) && <div className="text-[10px] font-bold uppercase tracking-widest px-1 py-1 mt-1" style={{ color: MUTED }}>Directory</div>}
                  </>
                )}
                {/* Departments */}
                {filteredDepts.length > 0 && (
                  <>
                    <div className="text-[10px] font-bold uppercase tracking-widest px-1 py-1" style={{ color: MUTED }}>Departments</div>
                    {filteredDepts.map((d) => (
                      <button key={d.id || d.key} data-testid={`rc-transfer-dept-${d.id || d.key}`} onClick={() => { transferToDepartment(d); setPanel(false); setSearch(""); }}
                        className="w-full flex items-center gap-2.5 py-2 px-1.5 rounded-lg hover:bg-slate-50 text-left">
                        <div className="w-8 h-8 rounded-full grid place-items-center text-white shrink-0" style={{ background: NAVY }}><Building2 size={15} /></div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] font-semibold truncate" style={{ color: NAVY_DEEP }}>{d.name}</div>
                          <div className="text-[11px] truncate" style={{ color: MUTED }}>{d.available_count ?? 0} available</div>
                        </div>
                      </button>
                    ))}
                    {filteredTargets.length > 0 && <div className="text-[10px] font-bold uppercase tracking-widest px-1 py-1 mt-1" style={{ color: MUTED }}>People</div>}
                  </>
                )}
                {/* Colleagues */}
                {filteredTargets.length === 0 && filteredDepts.length === 0 && filteredFavs.length === 0 ? (
                  <div className="text-[12px] py-3 text-center" style={{ color: MUTED }}>No matches.</div>
                ) : filteredTargets.map((t) => (
                  <button key={t.id} data-testid={`rc-transfer-target-${t.id}`} onClick={() => { warmTransferToColleague(t); setPanel(false); setSearch(""); }}
                    className="w-full flex items-center gap-2.5 py-2 px-1.5 rounded-lg hover:bg-slate-50 text-left">
                    <div className="w-8 h-8 rounded-full grid place-items-center text-white text-[11px] font-bold shrink-0" style={{ background: ROYAL }}>{initials(`${t.first_name || ""} ${t.last_name || ""}`)}</div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold truncate" style={{ color: NAVY_DEEP }}>{`${t.first_name || ""} ${t.last_name || ""}`.trim() || t.email}</div>
                      <div className="text-[11px] truncate flex items-center gap-1" style={{ color: MUTED }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: GREEN }} />{t.status || "available"}</div>
                    </div>
                    {t.extension && <span data-testid={`rc-transfer-ext-badge-${t.id}`} className="text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0" style={{ background: "rgba(3,40,102,0.07)", color: NAVY }}>x{t.extension}</span>}
                  </button>
                ))}
              </div>
              {/* Or warm-transfer to a raw desk extension / number */}
              <div className="mt-2.5 pt-2.5" style={{ borderTop: `1px solid ${HAIRLINE}` }}>
                <div className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: MUTED }}>Or dial an extension / cell number</div>
                <div className="flex gap-2">
                  <input data-testid="rc-transfer-ext-input" value={extNumber} onChange={(e) => setExtNumber(e.target.value)} placeholder="Ext (e.g. 101) or +1 cell #"
                    className="flex-1 rounded-lg border px-3 py-2 text-[13px] focus:outline-none" style={{ borderColor: HAIRLINE, color: NAVY_DEEP }} />
                  <button data-testid="rc-transfer-ext-confirm" onClick={submitExtension} disabled={!extNumber.trim()} className="h-9 px-3 rounded-lg text-[12px] font-bold text-white disabled:opacity-50" style={{ background: NAVY }}>Call</button>
                </div>
              </div>
              <button onClick={() => { setPanel(false); setSearch(""); setExtNumber(""); }} className="w-full h-9 mt-2 rounded-lg text-[13px] font-semibold" style={{ border: `1px solid ${HAIRLINE}`, color: NAVY }}>Cancel</button>
            </div>
          ) : (
            <div>
              {favorites.length > 0 && (
                <div className="mb-3" data-testid="rc-transfer-blind-favorites">
                  <div className="text-[10px] font-bold uppercase tracking-widest mb-1.5 flex items-center gap-1" style={{ color: MUTED }}><Star size={10} style={{ color: "#C9A800", fill: "#FFDA00" }} /> Favorites — tap to transfer</div>
                  <div className="flex flex-col gap-1 max-h-32 overflow-y-auto -mx-1 px-1">
                    {favorites.map((f) => (
                      <button key={f.id} data-testid={`rc-transfer-fav-${f.id}`} onClick={() => transferToFavorite(f)}
                        className="w-full flex items-center gap-2.5 py-1.5 px-1.5 rounded-lg hover:bg-slate-50 text-left">
                        <div className="w-7 h-7 rounded-full grid place-items-center text-white text-[11px] font-bold shrink-0" style={{ background: GREEN }}>{initials(f.name, f.phone_number)}</div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[12.5px] font-semibold truncate" style={{ color: NAVY_DEEP }}>{f.name}</div>
                          <div className="text-[11px] truncate" style={{ color: MUTED }}>{formatPhoneNumber(f.phone_number)}</div>
                        </div>
                        <PhoneForwarded size={13} className="shrink-0" style={{ color: NAVY }} />
                      </button>
                    ))}
                  </div>
                  <div className="text-[10px] font-bold uppercase tracking-widest mt-2.5 mb-1.5" style={{ color: MUTED }}>Or type an extension / cell number</div>
                </div>
              )}
              {favorites.length === 0 && <div className="text-[11px] font-bold uppercase tracking-widest mb-1.5" style={{ color: MUTED }}>Transfer to extension or cell number</div>}
              <input data-testid="rc-transfer-number-input" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="Ext (e.g. 101) or +1 (___) ___-____"
                className="w-full rounded-lg border px-3 py-2 text-[14px] focus:outline-none" style={{ borderColor: HAIRLINE, color: NAVY_DEEP }} />
              <div className="flex gap-2 mt-2">
                <button onClick={() => { setPanel(false); setTarget(""); }} className="flex-1 h-9 rounded-lg text-[13px] font-semibold" style={{ border: `1px solid ${HAIRLINE}`, color: NAVY }}>Cancel</button>
                <button data-testid="rc-transfer-number-confirm" onClick={submitNumber} disabled={!target.trim()} className="flex-1 h-9 rounded-lg text-[13px] font-bold text-white disabled:opacity-50" style={{ background: NAVY }}>Transfer</button>
              </div>
            </div>
          )}
        </div>
      ) : panel === "merge" ? (
        <div className="w-full mb-4" data-testid="rc-merge-panel">
          <div className="text-[11px] font-bold uppercase tracking-widest mb-1.5" style={{ color: MUTED }}>Add / merge number</div>
          <input data-testid="rc-merge-input" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="+1 (___) ___-____"
            className="w-full rounded-lg border px-3 py-2 text-[14px] focus:outline-none" style={{ borderColor: HAIRLINE, color: NAVY_DEEP }} />
          <div className="flex gap-2 mt-2">
            <button onClick={() => { setPanel(false); setTarget(""); }} className="flex-1 h-9 rounded-lg text-[13px] font-semibold" style={{ border: `1px solid ${HAIRLINE}`, color: NAVY }}>Cancel</button>
            <button data-testid="rc-merge-confirm" onClick={submitNumber} disabled={!target.trim()} className="flex-1 h-9 rounded-lg text-[13px] font-bold text-white disabled:opacity-50" style={{ background: NAVY }}>Merge</button>
          </div>
        </div>
      ) : panel === "park" ? (
        <div className="w-full mb-4" data-testid="rc-park-panel">
          <div className="flex items-center mb-1.5">
            <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: MUTED }}>{editParkSlots ? "Edit slot labels" : "Park caller in a slot"}</span>
            <button data-testid="rc-park-edit-toggle" onClick={() => setEditParkSlots((v) => !v)} className="ml-auto flex items-center gap-1 text-[11px] font-semibold" style={{ color: NAVY }}>
              <Pencil size={12} /> {editParkSlots ? "Done" : "Edit"}
            </button>
          </div>

          {editParkSlots ? (
            <div className="max-h-56 overflow-y-auto -mx-1 px-1" data-testid="rc-park-edit-list">
              {(parkSlots || []).map((s) => (
                <div key={s.slot} className="mb-2.5" data-testid={`rc-park-edit-${s.slot}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-6 h-6 rounded grid place-items-center text-[10px] font-bold text-white shrink-0" style={{ background: slotHex(s.color) }}>P{s.slot}</span>
                    <input data-testid={`rc-park-label-input-${s.slot}`} defaultValue={s.label || ""} placeholder="e.g. Spanish line"
                      onBlur={(e) => { const v = e.target.value.trim(); if (v !== (s.label || "")) saveSlotConfig(s.slot, { label: v }); }}
                      className="flex-1 rounded-md border px-2 py-1 text-[12px] focus:outline-none" style={{ borderColor: HAIRLINE, color: NAVY_DEEP }} />
                  </div>
                  <div className="flex gap-1.5 pl-8">
                    {PALETTE.map((c) => (
                      <button key={c} data-testid={`rc-park-color-${s.slot}-${c}`} onClick={() => saveSlotConfig(s.slot, { color: c })}
                        className="w-5 h-5 rounded-full" style={{ background: slotHex(c), outline: s.color === c ? `2px solid ${NAVY_DEEP}` : "none", outlineOffset: 1 }} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-5 gap-1.5 mb-1">
                {(parkSlots || []).map((s) => (
                  <button key={s.slot} data-testid={`rc-park-slot-${s.slot}`} disabled={s.occupied}
                    onClick={() => { parkActiveCall(s.slot); setPanel(false); }}
                    className="h-11 rounded-lg text-[13px] font-bold grid place-items-center text-white disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ background: slotHex(s.color) }}
                    title={`${slotLabel(s)}${s.occupied ? " · occupied" : ""}`}
                  >
                    P{s.slot}
                  </button>
                ))}
              </div>
              <div className="text-[10px] mb-2.5 leading-tight" style={{ color: MUTED }}>
                {(parkSlots || []).filter((s) => s.label).map((s) => slotLabel(s)).join(" · ") || "Tap Edit to name slots"}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setPanel(false)} className="flex-1 h-9 rounded-lg text-[13px] font-semibold" style={{ border: `1px solid ${HAIRLINE}`, color: NAVY }}>Cancel</button>
                <button data-testid="rc-park-auto" onClick={() => { parkActiveCall(null); setPanel(false); }} disabled={(parkSlots || []).every((s) => s.occupied)} className="flex-1 h-9 rounded-lg text-[13px] font-bold text-white disabled:opacity-50" style={{ background: AMBER }}>Auto-park</button>
              </div>
            </>
          )}
        </div>
      ) : showKeypad ? (
        <div className="grid grid-cols-3 gap-2 w-full mb-4">
          {KEYS.map((k) => (
            <button key={k} data-testid={`rc-dtmf-${k}`} onClick={() => sendDTMF?.(k)} className="h-11 rounded-lg text-[18px] font-semibold" style={{ background: "#fff", border: `1px solid ${HAIRLINE}`, color: NAVY_DEEP }}>{k}</button>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-x-5 gap-y-4 mb-6">
          {controls.map((c) => {
            const I = c.icon;
            const activeCol = c.activeColor || NAVY;
            const on = c.active;
            return (
              <button key={c.key} onClick={c.onClick} className="flex flex-col items-center gap-1.5 group" data-testid={`rc-call-${c.key}`}>
                <div className="rounded-full grid place-items-center transition-transform duration-200 group-active:scale-95 group-hover:brightness-[0.98]" style={{
                  width: 58, height: 58,
                  border: `1.5px solid ${on ? activeCol : (c.hot ? "rgba(3,40,102,0.22)" : HAIRLINE)}`,
                  background: on ? activeCol : (c.hot ? "rgba(3,40,102,0.06)" : "rgba(255,255,255,0.9)"),
                  color: on ? "#fff" : (c.hot ? NAVY : NAVY_DEEP),
                  boxShadow: on ? `0 8px 18px ${activeCol}55` : "0 2px 7px rgba(3,40,102,0.08)",
                }}>
                  <I size={21} strokeWidth={1.9} />
                </div>
                <span className="text-[11px] font-semibold" style={{ color: on ? activeCol : (c.hot ? NAVY : MUTED) }}>{c.label}</span>
              </button>
            );
          })}
        </div>
      )}

      <button data-testid="rc-call-end" onClick={endCall} className="w-16 h-16 rounded-full grid place-items-center text-white transition-transform duration-200 active:scale-95" style={{ background: RED, boxShadow: `0 10px 24px ${RED}55` }}><PhoneOff size={24} /></button>
    </Shell>
  );
}

const POS_KEY = "avida.softphone.pos";
// Load a saved drop position, clamped to the current viewport (card ~342px wide).
function loadSavedPos() {
  try {
    const raw = localStorage.getItem(POS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (typeof p?.x !== "number" || typeof p?.y !== "number") return null;
    const x = Math.max(8, Math.min(p.x, window.innerWidth - 342 - 8));
    const y = Math.max(8, Math.min(p.y, window.innerHeight - 48));
    return { x, y };
  } catch { return null; }
}

function Shell({ title, onClose, children }) {
  // Defaults to the top-right; the header is a drag handle so users can move it
  // anywhere on screen. Restores the last dropped position from localStorage.
  const nodeRef = useRef(null);
  const [pos, setPos] = useState(loadSavedPos);

  const startDrag = (e) => {
    // Don't start a drag from the close button.
    if (e.target.closest?.('[data-testid="rc-softphone-close"]')) return;
    const node = nodeRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const offX = e.clientX - rect.left;
    const offY = e.clientY - rect.top;
    const w = rect.width, h = rect.height;
    const onMove = (ev) => {
      const nx = Math.max(8, Math.min(ev.clientX - offX, window.innerWidth - w - 8));
      const ny = Math.max(8, Math.min(ev.clientY - offY, window.innerHeight - 48));
      setPos({ x: nx, y: ny });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      // Persist the final drop position so it reopens here next time.
      setPos((p) => { if (p) { try { localStorage.setItem(POS_KEY, JSON.stringify(p)); } catch {} } return p; });
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  return (
    <div
      ref={nodeRef}
      data-testid="rc-softphone"
      className="flex flex-col"
      style={{
        position: "fixed",
        top: pos ? pos.y : 76,
        left: pos ? pos.x : "auto",
        right: pos ? "auto" : 20,
        zIndex: 70,
        width: 342, fontFamily: FONT_BODY, borderRadius: 24, overflow: "hidden",
        background: "rgba(255,255,255,0.86)",
        backdropFilter: "blur(24px) saturate(150%)", WebkitBackdropFilter: "blur(24px) saturate(150%)",
        border: "1px solid rgba(255,255,255,0.65)",
        boxShadow: "0 28px 70px rgba(3,40,102,0.34), inset 0 1px 0 rgba(255,255,255,0.85)",
      }}>
      {/* ── Branded header (also the drag handle) ── */}
      <div
        onPointerDown={startDrag}
        className="relative px-5 pt-4 pb-3.5 flex items-center gap-2.5 select-none"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_DEEP} 100%)`, cursor: "grab", touchAction: "none" }}
      >
        {/* soft brand glow */}
        <div className="absolute -top-8 -right-6 w-28 h-28 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(255,218,0,0.18) 0%, transparent 70%)" }} />
        <div className="w-9 h-9 rounded-xl bg-white grid place-items-center shrink-0 p-1" style={{ boxShadow: "0 3px 10px rgba(0,0,0,0.18)" }}>
          <img src="/avida-logo-mark.png" alt="Avida" className="w-full h-full object-contain" draggable={false} />
        </div>
        <div className="min-w-0 relative">
          <div className="text-white font-black text-[13.5px] leading-tight tracking-tight" style={{ fontFamily: FONT_HEAD }}>Avida Health</div>
          <div className="text-[9.5px] font-bold uppercase tracking-[0.18em]" style={{ color: "#FFDA00" }}>{title}</div>
        </div>
        <button data-testid="rc-softphone-close" onClick={onClose} className="ml-auto w-8 h-8 grid place-items-center rounded-lg transition-colors hover:bg-white/15 relative" style={{ color: "rgba(255,255,255,0.85)", cursor: "pointer" }}><X size={16} /></button>
        {/* gold underline accent */}
        <div className="absolute left-0 right-0 bottom-0 h-[2.5px]" style={{ background: "linear-gradient(90deg, #FFDA00 0%, rgba(255,218,0,0.15) 60%, transparent 100%)" }} />
      </div>
      {/* ── Body ── */}
      <div className="flex flex-col items-center px-6 py-5">
        {children}
      </div>
    </div>
  );
}
